import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { DropdownItemCheckbox, DropdownItemCheckboxGroup } from '@atlaskit/dropdown-menu';
import Button from '@atlaskit/button/new';
import Badge from '@atlaskit/badge';
import Textfield from '@atlaskit/textfield';
import SearchIcon from '@atlaskit/icon/core/search';
import SpacesIcon from '@atlaskit/icon-lab/core/spaces';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

const SpaceCheckboxItem = ({
	spaceId,
	name,
	isSelected,
	onClick,
}: {
	spaceId: string;
	name: string;
	isSelected: boolean;
	onClick: (spaceId: string) => void;
}) => {
	const handleClick = useCallback(() => onClick(spaceId), [onClick, spaceId]);
	return useMemo(
		() => (
			<DropdownItemCheckbox
				key={spaceId}
				id={`space-${spaceId}`}
				isSelected={isSelected}
				onClick={handleClick}
			>
				{name}
			</DropdownItemCheckbox>
		),
		[spaceId, name, isSelected, handleClick],
	);
};

const i18n = defineMessages({
	triggerLabel: {
		id: 'performance-insights.content-metrics.filters.space.trigger-label.non-final',
		defaultMessage: 'Space',
		description:
			'Label for the Space filter dropdown trigger button in the content metrics view controls bar. Opens a popup to search and filter content by Confluence space.',
	},
	searchPlaceholder: {
		id: 'performance-insights.content-metrics.filters.space.search-placeholder.non-final',
		defaultMessage: 'Search for a space',
		description:
			'Placeholder text for the search input in the Space filter popup. Allows admins to search for specific Confluence spaces to filter by.',
	},
	searchLabel: {
		id: 'performance-insights.content-metrics.filters.space.search-label.non-final',
		defaultMessage: 'Search spaces',
		description: 'Accessible label for the search input in the Space filter popup.',
	},
});

const styles = cssMap({
	popupContent: {
		width: '240px',
	},
	searchContainer: {
		paddingInline: token('space.150'),
		paddingBlock: token('space.100'),
	},
	listContainer: {
		maxHeight: '240px',
		overflowY: 'auto',
	},
});

export type SpaceOption = {
	id: string;
	name: string;
};

export type SpaceFilterProps = {
	spaces: SpaceOption[];
	selectedSpaceIds: string[];
	onSelectionChange: (selectedSpaceIds: string[]) => void;
};

export const SpaceFilter = ({ spaces, selectedSpaceIds, onSelectionChange }: SpaceFilterProps) => {
	const { formatMessage } = useIntl();
	const { Popup } = useChoreographerAssets();
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');

	const filteredSpaces = useMemo(
		() => spaces.filter((space) => space.name.toLowerCase().includes(searchTerm.toLowerCase())),
		[spaces, searchTerm],
	);

	const handleClick = useCallback(
		(spaceId: string) => {
			if (selectedSpaceIds.includes(spaceId)) {
				onSelectionChange(selectedSpaceIds.filter((id) => id !== spaceId));
			} else {
				onSelectionChange([...selectedSpaceIds, spaceId]);
			}
		},
		[selectedSpaceIds, onSelectionChange],
	);

	const handleSearchChange = useCallback((e: React.FormEvent<HTMLInputElement>) => {
		setSearchTerm(e.currentTarget.value);
	}, []);

	const handleClose = useCallback(() => {
		setIsOpen(false);
		setSearchTerm('');
	}, []);

	const hasSelections = selectedSpaceIds.length > 0;
	const extraCount = selectedSpaceIds.length - 1;

	const getTriggerLabel = () => {
		if (!hasSelections) {
			return formatMessage(i18n.triggerLabel);
		}
		const firstSelectedSpace = spaces.find((s) => s.id === selectedSpaceIds[0]);
		const firstName = firstSelectedSpace?.name ?? selectedSpaceIds[0];
		return `${formatMessage(i18n.triggerLabel)}: ${firstName}`;
	};

	return (
		<Popup
			isOpen={isOpen}
			onClose={handleClose}
			placement="bottom-start"
			trigger={(triggerProps) => (
				<Button
					{...triggerProps}
					ref={triggerProps.ref}
					isSelected={hasSelections || isOpen}
					onClick={() => setIsOpen(!isOpen)}
					iconBefore={(iconProps) => <SpacesIcon {...iconProps} label="" />}
					iconAfter={(iconProps) => <ChevronDownIcon {...iconProps} label="" />}
				>
					<Inline alignBlock="center" space="space.050">
						{getTriggerLabel()}
						{extraCount > 0 && <Badge appearance="primary">{`+${extraCount}`}</Badge>}
					</Inline>
				</Button>
			)}
			content={() => (
				<Box xcss={styles.popupContent}>
					<Box xcss={styles.searchContainer}>
						<Textfield
							isCompact
							appearance="none"
							aria-label={formatMessage(i18n.searchLabel)}
							placeholder={formatMessage(i18n.searchPlaceholder)}
							value={searchTerm}
							onChange={handleSearchChange}
							elemBeforeInput={
								<span>
									<SearchIcon spacing="spacious" label="" />
								</span>
							}
							autoFocus
						/>
					</Box>
					<Box xcss={styles.listContainer} role="menu">
						<DropdownItemCheckboxGroup id="space-filter">
							{filteredSpaces.map((space) => (
								<SpaceCheckboxItem
									key={space.id}
									spaceId={space.id}
									name={space.name}
									isSelected={selectedSpaceIds.includes(space.id)}
									onClick={handleClick}
								/>
							))}
						</DropdownItemCheckboxGroup>
					</Box>
				</Box>
			)}
		/>
	);
};
